import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { ArrowUpRight } from "lucide-react";

const steps = [
  {
    n: "01",
    title: "Erstgespräch",
    text: "Vertrauliches Gespräch zur Einordnung der Ausgangslage, der Motivation und der wesentlichen Erwartungen. Keine Verpflichtung, klare Erwartungssteuerung.",
  },
  {
    n: "02",
    title: "Analyse",
    text: "Strukturierte Bewertung des Geschäftsmodells, der Märkte, der wirtschaftlichen Lage und der unternehmerischen Substanz. Aufbau eines gemeinsamen Verständnisses.",
  },
  {
    n: "03",
    title: "Due Diligence",
    text: "Tiefenprüfung der wirtschaftlichen, rechtlichen, steuerlichen und operativen Dimensionen – mit ausgewählten Fachpartnern und unter strikter Vertraulichkeit.",
  },
  {
    n: "04",
    title: "Strukturierung",
    text: "Definition der Beteiligungsstruktur, der Governance, der Finanzierung sowie der Rolle aller Beteiligten. Vertragliche Abbildung mit erfahrenen Beratern.",
  },
  {
    n: "05",
    title: "Umsetzung",
    text: "Übergang in die operative Realität. Stabilisierung der wesentlichen Prozesse, Einrichtung von Reporting und Governance, klare Verantwortlichkeiten.",
  },
  {
    n: "06",
    title: "Langfristige Partnerschaft",
    text: "Kontinuierliche Begleitung in Strategie, Wachstum und Weiterentwicklung. Wir denken in Jahrzehnten, nicht in Quartalen.",
  },
];

const principles = [
  "Diskretion in jeder Phase",
  "Klare Kommunikation, schnelle Entscheidungen",
  "Pragmatische Umsetzung statt Bürokratie",
  "Langfristige Verantwortung statt Exit-Druck",
];

export default function Ablauf() {
  useRevealOnScroll();

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="Ablauf einer Beteiligung | Wagenhofer Capital München"
        description="Vom Erstgespräch bis zur langfristigen Partnerschaft: Der strukturierte Beteiligungsprozess von Wagenhofer Capital in sechs Schritten."
        path="/ablauf"
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "Ablauf", to: "/ablauf" }]} />

      <PageHeader
        testId="page-process"
        overline="Ablauf einer Beteiligung"
        title={
          <>
            Sechs Schritte.
            <br />
            <span className="text-zinc-400">Klar. Strukturiert. Diskret.</span>
          </>
        }
        subtitle="Unser Beteiligungsprozess folgt einer klaren Logik. Jeder Schritt hat einen definierten Zweck – und schafft die Grundlage für eine belastbare, langfristige Partnerschaft."
      />

      <section
        data-testid="section-process"
        className="section-y border-b border-white/5"
      >
        <div className="container-x">
          <ol className="relative space-y-px">
            {steps.map((step, idx) => (
              <li
                key={step.n}
                data-testid={`step-${step.n}`}
                className="reveal grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-t border-white/10 group"
                style={{ transitionDelay: `${idx * 60}ms` }}
              >
                <div className="md:col-span-2">
                  <div className="font-display text-3xl md:text-5xl text-[#D4AF37] tracking-tighter">
                    {step.n}
                  </div>
                </div>
                <div className="md:col-span-4">
                  <h2 className="font-display text-xl md:text-2xl lg:text-3xl tracking-tight font-medium leading-[1.1]">
                    {step.title}
                  </h2>
                </div>
                <div className="md:col-span-6 text-zinc-400 leading-relaxed font-light text-[15px] md:text-base">
                  {step.text}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-y bg-[#070707] border-b border-white/5">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
          <div className="md:col-span-5 reveal">
            <div className="overline mb-6">Prinzipien</div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight font-medium leading-[1.08]">
              Vier Prinzipien,
              <br />
              <span className="text-zinc-400">über alle Phasen hinweg.</span>
            </h2>
          </div>
          <ul className="md:col-span-7 md:pl-8 reveal delay-1 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {principles.map((p, idx) => (
              <li
                key={p}
                className="border border-white/10 p-6 text-zinc-200 text-sm md:text-base leading-relaxed"
              >
                <span className="text-[#D4AF37] text-xs tracking-[0.2em] mr-3">
                  0{idx + 1}
                </span>
                {p}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="reveal">
            <div className="overline mb-3">Nächster Schritt</div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
              Erstgespräch vereinbaren.
            </h2>
          </div>
          <Link
            to="/kontakt"
            data-testid="process-cta-contact"
            className="reveal delay-1 group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
          >
            Kontakt aufnehmen
            <ArrowUpRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.5}
            />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
